import { Listing, ListingStatus, ApiResult } from '../../../types';
import { SearchFilters } from '../../repository';
import { filterListings } from '../../domain/listings/filters';
import { sortListings } from '../../domain/listings/sorting';
import { DB_KEY } from './constants';
import { ListingMapper } from '../mappers/listing.mapper';
import { validatedEnv } from '../../../utils/env';

export class LocalListingsRepo {
    async getListings(filters?: SearchFilters): Promise<Listing[]> {
        const allListings = this.loadListingsFromStorage()
            .map(r => ListingMapper.toDomain(r));

        const filtered = filterListings(allListings, filters);
        const sorted = sortListings(filtered, filters?.sort);
        return typeof filters?.limit === 'number' ? sorted.slice(0, filters.limit) : sorted;
    }

    async getListingsCount(): Promise<number | null> {
        try {
            return (await this.getListings()).length;
        } catch (e) {
            console.error('Local getListingsCount error', e);
            return null;
        }
    }

    async getHomepageListings(): Promise<{ listings: Listing[]; totalCount: number | null }> {
        try {
            const allListings = await this.getListings({ limit: 4, sort: 'newest' });
            const totalCount = await this.getListingsCount();
            return { listings: allListings, totalCount };
        } catch (e) {
            console.error('Local getHomepageListings error', e);
            throw e;
        }
    }

    async getAdminListings(): Promise<Listing[]> {
        return this.loadListingsFromStorage().map(r => ListingMapper.toDomain(r));
    }

    async getListingById(id: string): Promise<Listing | undefined> {
        const all = this.loadListingsFromStorage();
        const found = all.find(x => x.id === id);
        if (!found) return undefined;
        return ListingMapper.toDomain(found);
    }

    async getMyListings(userId: string): Promise<Listing[]> {
        const all = this.loadListingsFromStorage();
        return all
            .map(r => ListingMapper.toDomain(r))
            .filter(l => l.seller.id === userId);
    }

    async addListing(listing: Listing, userId: string): Promise<ApiResult<Listing>> {
        const all = this.loadListingsFromStorage();
        const row = ListingMapper.toRow(listing, userId);
        all.push(row);
        this.saveListings(all);
        return { success: true, data: listing };
    }

    async updateListing(listing: Listing, userId: string): Promise<ApiResult<Listing>> {
        const all = this.loadListingsFromStorage();
        const idx = all.findIndex(x => x.id === listing.id);
        if (idx === -1) return { success: false, error: 'Not found' };

        const updatedRow = ListingMapper.toRow(listing, userId);
        all[idx] = updatedRow;
        this.saveListings(all);
        return { success: true, data: ListingMapper.toDomain(updatedRow) };
    }

    async updateListingStatus(id: string, status: ListingStatus): Promise<boolean> {
        const all = this.loadListingsFromStorage();
        const idx = all.findIndex(x => x.id === id);
        if (idx === -1) return false;
        all[idx].status = status;
        this.saveListings(all);
        return true;
    }

    async updateListingTrust(id: string, fields: Record<string, string | boolean | null>): Promise<boolean> {
        const all = this.loadListingsFromStorage();
        const idx = all.findIndex(x => x.id === id);
        if (idx === -1) return false;
        Object.assign(all[idx], fields);
        this.saveListings(all);
        return true;
    }

    async updateListingImages(id: string, images: string[]): Promise<boolean> {
        const all = this.loadListingsFromStorage();
        const idx = all.findIndex(x => x.id === id);
        if (idx === -1) return false;
        all[idx].images = JSON.stringify(images);
        this.saveListings(all);
        return true;
    }

    async deleteListing(id: string): Promise<ApiResult<void>> {
        let all = this.loadListingsFromStorage();
        const initialLen = all.length;
        all = all.filter(x => x.id !== id);
        if (all.length !== initialLen) {
            this.saveListings(all);
            return { success: true, data: undefined };
        }
        return { success: false, error: 'Not found' };
    }

    async toggleListingStatus(listingId: string, currentStatus: string): Promise<{ success: boolean; error?: string }> {
        const newStatus = currentStatus === 'Schválený' ? ListingStatus.HIDDEN : ListingStatus.APPROVED;
        const ok = await this.updateListingStatus(listingId, newStatus);
        return { success: ok };
    }

    async updateListingProfileRequirement(_sellerId: string, _required: boolean): Promise<{ success: boolean; error?: string }> {
        if (validatedEnv.PROD) {
            console.error('CRITICAL: Local fallbacks are strictly disabled in production.');
            return { success: false, error: 'Write operations are disabled in fallback mode.' };
        }
        return { success: true };
    }


    async getListingAnalytics(_id: string, days?: number): Promise<any[]> {
        return Array.from({ length: days || 7 }).map((_, i) => ({
            date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
            views: 0,
            clicks: 0
        })).reverse();
    }

    async getListingsAnalyticsBulk(_ids: string[], days?: number): Promise<any[]> {
        return Array.from({ length: days || 7 }).map((_, i) => ({
            date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
            views: 0,
            clicks: 0
        })).reverse();
    }

    async trackListingClick(_listingId: string): Promise<void> { }

    private loadListingsFromStorage(): any[] {
        try {
            const stored = localStorage.getItem(DB_KEY);
            if (stored) return JSON.parse(stored);
        } catch (e) {
            console.error('Failed to parse localStorage listings', e);
        }
        return [];
    }

    private saveListings(rows: any[]) {
        if (validatedEnv.PROD) {
            console.error('CRITICAL: Local fallbacks are strictly disabled in production.');
            throw new Error('Local fallbacks are strictly disabled in production.');
        }
        try {
            localStorage.setItem(DB_KEY, JSON.stringify(rows));
        } catch (e) {
            console.error('Failed to save listings', e);
        }
    }
}
